import { useState, useEffect } from 'react';
import { getNextPublishTime, NextPublishInfo } from '@/utils/scheduler';

interface CountdownParts {
  hours: string;
  minutes: string;
  seconds: string;
}

function getCountdown(target: Date): CountdownParts | null {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return null;
  const totalSecs = Math.floor(diff / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return {
    hours: String(h).padStart(2, '0'),
    minutes: String(m).padStart(2, '0'),
    seconds: String(s).padStart(2, '0'),
  };
}

interface Props {
  variant?: 'sidebar' | 'banner';
}

export default function NextPublishCountdown({ variant = 'sidebar' }: Props) {
  const [info, setInfo] = useState<NextPublishInfo | null>(null);
  const [countdown, setCountdown] = useState<CountdownParts | null>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const next = getNextPublishTime();
    setInfo(next);
    if (next) setCountdown(getCountdown(next.date));
  }, []);

  useEffect(() => {
    if (!info) return;
    const interval = setInterval(() => {
      const cd = getCountdown(info.date);
      if (!cd) {
        // Article just dropped — refresh info
        const next = getNextPublishTime();
        setInfo(next);
        setCountdown(next ? getCountdown(next.date) : null);
        setPulse(true);
        setTimeout(() => setPulse(false), 1500);
      } else {
        setCountdown(cd);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [info]);

  if (!info || !countdown) return null;

  const whenLabel = info.isToday
    ? `Today · ${info.slotLabel}`
    : info.isTomorrow
    ? `Tomorrow · ${info.slotLabel}`
    : info.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  if (variant === 'banner') {
    return (
      <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 py-4 px-6 bg-stone-900 dark:bg-stone-800 transition-all ${pulse ? 'bg-rose-900' : ''}`}>
        <div className="flex items-center gap-2 text-stone-400 text-xs">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-time-line text-rose-400" />
          </div>
          <span className="uppercase tracking-widest font-semibold text-rose-400">Next article drops</span>
          <span className="text-stone-500">{whenLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          {[
            { value: countdown.hours, label: 'hr' },
            { value: countdown.minutes, label: 'min' },
            { value: countdown.seconds, label: 'sec' },
          ].map(({ value, label }, i) => (
            <>
              {i > 0 && <span key={`sep-${i}`} className="text-stone-600 text-sm font-bold mx-0.5">:</span>}
              <div key={label} className="flex flex-col items-center">
                <span className="text-white font-bold text-lg leading-none tabular-nums w-8 text-center"
                  style={{ fontFamily: "'Share Tech Mono', monospace" }}>
                  {value}
                </span>
                <span className="text-stone-500 text-xs uppercase tracking-wider mt-0.5">{label}</span>
              </div>
            </>
          ))}
        </div>
      </div>
    );
  }

  // Sidebar variant
  return (
    <div className={`rounded-xl border border-stone-100 dark:border-stone-700 bg-white dark:bg-stone-800 overflow-hidden transition-all ${pulse ? 'border-rose-300' : ''}`}>
      {/* Header strip */}
      <div className="bg-stone-900 dark:bg-stone-950 px-4 py-2.5 flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-time-line text-rose-400 text-sm" />
        </div>
        <span className="text-xs font-semibold text-rose-400 uppercase tracking-widest">Next Article</span>
      </div>

      <div className="px-4 py-4">
        {/* When label */}
        <p className="text-xs text-stone-400 dark:text-stone-500 mb-3 flex items-center gap-1.5">
          <i className="ri-calendar-event-line" />
          {whenLabel}
        </p>

        {/* Countdown digits */}
        <div className="flex items-end gap-1 mb-3">
          {[
            { value: countdown.hours, label: 'Hours' },
            { value: countdown.minutes, label: 'Min' },
            { value: countdown.seconds, label: 'Sec' },
          ].map(({ value, label }, i) => (
            <>
              {i > 0 && (
                <span key={`sep-${i}`} className="text-stone-300 dark:text-stone-600 font-bold text-xl pb-4 mx-0.5">:</span>
              )}
              <div key={label} className="flex flex-col items-center flex-1">
                <div className="w-full bg-stone-50 dark:bg-stone-900 rounded-lg py-2 text-center">
                  <span
                    className="text-2xl font-bold text-stone-800 dark:text-stone-100 tabular-nums leading-none"
                    style={{ fontFamily: "'Share Tech Mono', monospace" }}
                  >
                    {value}
                  </span>
                </div>
                <span className="text-xs text-stone-400 dark:text-stone-500 mt-1 uppercase tracking-wider">{label}</span>
              </div>
            </>
          ))}
        </div>

        {/* Publish schedule note */}
        <p className="text-xs text-stone-400 dark:text-stone-500 leading-relaxed">
          We publish <strong className="text-stone-600 dark:text-stone-300">3 articles daily</strong> at 8am, 1pm &amp; 7pm.
        </p>
      </div>
    </div>
  );
}
